import { useSpinDelay } from 'spin-delay'
import { cn } from '#app/utils/misc.tsx'
import { Button, type ButtonVariant } from './button.tsx'
import { Icon } from './icon.tsx'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from './tooltip.tsx'

export const StatusButton = ({
	message,
	status,
	className,
	children,
	pendingLabel,
	spinDelay,
	...props
}: React.ComponentProps<'button'> &
	ButtonVariant & {
		status: 'pending' | 'success' | 'error' | 'idle'
		message?: string | null
		/**
		 * ADR-025 (7.4): the label the button shows while pending (HIG:
		 * "Checkout" → "Checking out…"). Swapped in together with the spinner,
		 * after the spin delay, so a fast submit never flashes.
		 */
		pendingLabel?: React.ReactNode
		spinDelay?: Parameters<typeof useSpinDelay>[1]
	}) => {
	const delayedPending = useSpinDelay(status === 'pending', {
		delay: 400,
		minDuration: 300,
		...spinDelay,
	})
	const companion = {
		pending: delayedPending ? (
			<div
				role="status"
				className="inline-flex size-6 items-center justify-center"
			>
				<Icon
					name="update"
					className="animate-spin motion-reduce:animate-none"
					title="bezig"
				/>
			</div>
		) : null,
		success: (
			<div
				role="status"
				className="inline-flex size-6 items-center justify-center"
			>
				<Icon name="check" title="gelukt" />
			</div>
		),
		error: (
			// ADR-025 (9.4 / ADR-020 "no red state"): the warm pair, like the
			// passkeys notice — not the destructive red
			<div
				role="status"
				className="bg-oy-warm-soft inline-flex size-6 items-center justify-center rounded-full"
			>
				<Icon name="cross-1" className="text-oy-warm" title="mislukt" />
			</div>
		),
		idle: null,
	}[status]

	return (
		<Button className={cn('flex justify-center gap-4', className)} {...props}>
			<div>
				{status === 'pending' && delayedPending && pendingLabel
					? pendingLabel
					: children}
			</div>
			{message ? (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger>{companion}</TooltipTrigger>
						<TooltipContent>{message}</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			) : (
				companion
			)}
		</Button>
	)
}
StatusButton.displayName = 'Button'
